//changed due to package upgrade
import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { alpha, Box, Button, FormControlLabel, Switch } from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import jwtAxios from '@projectK/services/auth/JWT';
import PropTypes from 'prop-types';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import { useRolePermissions, hasPermission } from '@projectK/context/RolesProvider';
import IntlMessages from '@projectK/helpers/IntlMessages';

const NotificationPreferances = ({ companyTheme }) => {
  const infoViewActionsContext = useInfoViewActionsContext();
  const { verbiageSettings } = useApplicationSettings();
  const { rolePermissions } = useRolePermissions();
  const { user } = useAuthUser();
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    fetchSettings();
  }, []);
  const fetchSettings = async () => {
    try {
      const response = await jwtAxios.get(`/notification-settings/${user.id}`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching Notifications settings:', error);
    }
  };

  return (
    <>
      <Box>
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            attendance: settings?.attendance === 1,
            failed_transaction: settings?.failed_transaction === 1,
            feedback: settings?.feedback === 1,
            join_company: settings?.join_company === 1,
            join_waitlist: settings?.join_waitlist === 1,
            new_user: settings?.new_user === 1,
            processed_waitlist: settings?.processed_waitlist === 1,
            purchase: settings?.purchase === 1,
            offline_payment: settings?.offline_payment === 1,
            new_attendee: settings?.new_attendee === 1,
            refund: settings?.refund === 1,
            product_expiration: settings?.product_expiration === 1,
            scheduled_purchase_receipt_by_admin:
              settings?.scheduled_purchase_receipt_by_admin === 1,
            offline_payment_received: settings?.offline_payment_received === 1,
            credits_expiration: settings?.credits_expiration === 1,
            remainder_notifications: settings?.remainder_notifications === 1,
            paymentDue_notifications: settings?.paymentDue_notifications === 1,
          }}
          onSubmit={(values, { setSubmitting }) => {
            const fields = [
              'attendance',
              'failed_transaction',
              'feedback',
              'join_company',
              'join_waitlist',
              'new_user',
              'processed_waitlist',
              'purchase',
              'offline_payment',
              'new_attendee',
              'refund',
              'product_expiration',
              'scheduled_purchase_receipt_by_admin',
              'offline_payment_received',
              'credits_expiration',
              'remainder_notifications',
              'paymentDue_notifications',
            ];
            const requestData = Object.fromEntries(
              fields.map((key) => [key, values[key] === true ? 1 : 0]),
            );
            setSubmitting(true);
            jwtAxios
              .post(`/notification-settings/${user.id}`, requestData)
              .then((response) => {
                setSubmitting(false);
                if (response.status === 201 || response.status === 200) {
                  infoViewActionsContext.showMessage(
                    'Notification Preferences Updated Successfully',
                  );
                  fetchSettings();
                } else {
                  infoViewActionsContext.showMessage(
                    'Error Updating Notifications Preferences ',
                  );
                }
              })
              .catch((error) => {
                setSubmitting(false);
                infoViewActionsContext.fetchError(error.response.data.error);
              });
          }}
        >
          {({ values, setFieldValue, handleChange, resetForm }) => {
            return (
              <Form noValidate autoComplete='off'>
                <Grid size={{ xs: 12, md: 12 }}>
                  <AppGridContainer>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='attendance'
                            checked={values.attendance}
                            onChange={(e) => {
                              setFieldValue('attendance', e.target.checked);
                            }}
                          />
                        }
                        label='Attendance Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='feedback'
                            checked={values.feedback}
                            onChange={(e) => {
                              setFieldValue('feedback', e.target.checked);
                            }}
                          />
                        }
                        label='Feedback Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='join_waitlist'
                            checked={values.join_waitlist}
                            onChange={(e) => {
                              setFieldValue('join_waitlist', e.target.checked);
                            }}
                          />
                        }
                        label='Join Waitlist Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='processed_waitlist'
                            checked={values.processed_waitlist}
                            onChange={(e) => {
                              setFieldValue(
                                'processed_waitlist',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Confirm Waitlist Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='new_user'
                            checked={values.new_user}
                            onChange={(e) => {
                              setFieldValue('new_user', e.target.checked);
                            }}
                          />
                        }
                        label='New User Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='new_attendee'
                            checked={values.new_attendee}
                            onChange={(e) => {
                              setFieldValue('new_attendee', e.target.checked);
                            }}
                          />
                        }
                        label='New Attendee Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='purchase'
                            checked={values.purchase}
                            onChange={(e) => {
                              setFieldValue('purchase', e.target.checked);
                            }}
                          />
                        }
                        label='Purchase Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='offline_payment'
                            checked={values.offline_payment}
                            onChange={(e) => {
                              setFieldValue(
                                'offline_payment',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Offline Payment Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='failed_transaction'
                            checked={values.failed_transaction}
                            onChange={(e) => {
                              setFieldValue(
                                'failed_transaction',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Failed Transaction Notification'
                      />
                    </Grid>

                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='refund'
                            checked={values.refund}
                            onChange={(e) => {
                              setFieldValue('refund', e.target.checked);
                            }}
                          />
                        }
                        label='Refund Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='offline_payment_received'
                            checked={values.offline_payment_received}
                            onChange={(e) => {
                              setFieldValue(
                                'offline_payment_received',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Offline Payment Received Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='product_expiration'
                            checked={values.product_expiration}
                            onChange={(e) => {
                              setFieldValue(
                                'product_expiration',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label={`${
                          verbiageSettings?.product_type || 'Product'
                        } Expiration Notification`}
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='scheduled_purchase_receipt_by_admin'
                            checked={values.scheduled_purchase_receipt_by_admin}
                            onChange={(e) => {
                              setFieldValue(
                                'scheduled_purchase_receipt_by_admin',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Recurring Transaction Notification'
                      />
                    </Grid>

                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='credits_expiration'
                            checked={values.credits_expiration}
                            onChange={(e) => {
                              setFieldValue(
                                'credits_expiration',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Credit Expiration Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='remainder_notifications'
                            checked={values.remainder_notifications}
                            onChange={(e) => {
                              setFieldValue(
                                'remainder_notifications',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Reminder Notification'
                      />
                    </Grid>
                    <Grid  size={{ xs: 6, md: 12,lg: 6,xl:4  }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name='paymentDue_notifications'
                            checked={values.paymentDue_notifications}
                            onChange={(e) => {
                              setFieldValue(
                                'paymentDue_notifications',
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label='Payment Due Notification'
                      />
                    </Grid>

                    <br></br>
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {hasPermission(rolePermissions, 'My Locker - Settings', 'update') && (
                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                : alpha('#2196f3', 0.8),
                            },
                          }}
                          variant='contained'
                          type='submit'
                        >
                          Save
                        </Button>
                        )}

                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            ml: 2.5,
                            borderColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              borderColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8)
                                : alpha('#2196f3', 0.8),
                            },
                            color: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                          }}
                          variant='outlined'
                          onClick={(e) => {
                            e.preventDefault();
                            // Reset the form back to its initial state
                            resetForm();
                          }}
                        >
                          <IntlMessages id='common.cancel' />
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};

export default NotificationPreferances;
NotificationPreferances.propTypes = {
  companyTheme: PropTypes.object,
};
